import React, { useState, useImperativeHandle, useContext, forwardRef, useCallback } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import AppContext from '../../contexts/AppContext';

import Label from '../Label';
import Chip from '../Chip';
import EmailListManager from '../EmailListManager';
import {
  CardContainer,
  TwoColumnLayout,
  SectionTitle as SharedSectionTitle,
  FormContent,
  StepContainer,
  Section,
  Input,
  ErrorText,
} from '../PrebookingSharedComponents';
import CostAllocation from './CostAllocation';

import i18n from '../../i18n';

const SectionTitle = styled(SharedSectionTitle)`
  @media (max-width: 768px) {
    min-width: auto;
  }
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TranslationOrderInvoicingStep = forwardRef(({
  formData,
  updateField,
  addEmail,
  removeEmail,
  referenceChips = [],
}, ref) => {
  const { user } = useContext(AppContext);
  const [errors, setErrors] = useState({});

  const isInvoiceRefMandatory = user?.userGroup?.invoiceRefMandatory || formData.costAllocationGroup?.invoiceRefMandatory;
  const isCostAllocationMandatory = user?.userGroup?.invoiceRefUserGroupMandatory;

  const clearError = useCallback((field) => {
    setErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
  }, [setErrors]);

  const validateStep = () => {
    const newErrors = {};

    // Validate Cost Allocation if mandatory
    if (isCostAllocationMandatory && !formData.costAllocation) {
      newErrors.costAllocation = i18n('required');
    }

    // Validate that "Other" has an actual group selected
    if (formData.costAllocation === 'other' && !formData.costAllocationGroup?.id) {
      newErrors.costAllocation = i18n('required');
    }

    // Validate Invoice Ref (Additional Invoicing Info) if mandatory
    if (isInvoiceRefMandatory && !formData.additionalInvoicingInfo?.trim()) {
      newErrors.additionalInvoicingInfo = i18n('required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Expose validation method to parent via ref
  useImperativeHandle(ref, () => ({
    validate: validateStep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isCostAllocationMandatory, isInvoiceRefMandatory, formData]);

  const handleAdditionalInvoicingInfoChange = useCallback((e) => {
    updateField('additionalInvoicingInfo', e.target.value);
    if (e.target.value && errors.additionalInvoicingInfo) {
      clearError('additionalInvoicingInfo');
    }
  }, [updateField, errors, clearError]);

  return (
    <StepContainer>
      <CardContainer>
        <TwoColumnLayout>
          <SectionTitle>{i18n('invoicing')}</SectionTitle>
          <FormContent>
            {referenceChips.length > 0 && (
              <Section>
                <Label text={i18n('translation_order_specifying_invoicing_details')} />
                <ChipsRow>
                  {referenceChips.map((chipRef, index) => (
                    <Chip key={index} label={chipRef} disabled />
                  ))}
                </ChipsRow>
              </Section>
            )}

            <CostAllocation
              formData={formData}
              updateField={updateField}
              isCostAllocationMandatory={isCostAllocationMandatory}
              error={errors.costAllocation}
              clearError={clearError}
            />

            <Section>
              <Label text={i18n('additional_invoicing_information')} required={isInvoiceRefMandatory} />
              <Input
                type="text"
                value={formData.additionalInvoicingInfo || ''}
                onChange={handleAdditionalInvoicingInfoChange}
                placeholder={i18n('translation_order_additional_invoicing_placeholder')}
              />
              {errors.additionalInvoicingInfo && <ErrorText>{errors.additionalInvoicingInfo}</ErrorText>}
            </Section>

            <Section>
              <Label text={i18n('recipients_of_confirmation_and_change_notifications')} />
              <EmailListManager
                emails={formData.ccEmails}
                onAdd={addEmail}
                onRemove={removeEmail}
                placeholder="name@mail.com"
                addButtonText={i18n('add_email')}
                pinnedEmail={user?.email}
              />
            </Section>
          </FormContent>
        </TwoColumnLayout>
      </CardContainer>
    </StepContainer>
  );
});

TranslationOrderInvoicingStep.displayName = 'TranslationOrderInvoicingStep';

TranslationOrderInvoicingStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  addEmail: PropTypes.func.isRequired,
  removeEmail: PropTypes.func.isRequired,
  referenceChips: PropTypes.arrayOf(PropTypes.string),
};

export default TranslationOrderInvoicingStep;
